import { SidebarIcons } from '../../../types/sidebar';
import {
    CalendarIcon,
    DocumentsIcon,
    GreetingsIcon,
    HelloIcon,
    LinksIcon,
    MenuIcon,
    OrderIcon,
    PaymentIcon,
    SocialIcon,
    SurveyIcon,
    IconComponent,
} from '../../../neighbors/components/icons';
import { NeighborsOption, NeighborsHomeOption } from '../../../neighbors/types/NeighborsOption';

export const GREEN_BAR = '#5E9E7C';
export const BLUE_BAR = '#5A9BD5';

const neighborsOption = (
    name: string,
    path: string,
    iconName: SidebarIcons,
    barColor: string = GREEN_BAR,
): NeighborsOption => ({
    name,
    path,
    iconName,
    barColor,
});

const homeOption = (
    name: string,
    path: string,
    iconName: SidebarIcons,
    homeSublabel: string,
    homeIcon: IconComponent,
    barColor: string = GREEN_BAR,
): NeighborsHomeOption => ({
    ...neighborsOption(name, path, iconName, barColor),
    homeSublabel,
    homeIcon,
});

const toSidebarOption = ({ homeSublabel: _sublabel, homeIcon: _icon, ...rest }: NeighborsHomeOption): NeighborsOption => rest;

export const CalendarOption = homeOption('Calendar', '/calendar', 'calendar', 'Events & activities', CalendarIcon);
export const MenuOption = homeOption('Menu', '/menu', 'menu', "See today's meals", MenuIcon);
export const SocialOption = homeOption('Social', '/resident-social', 'social', 'Connect with others', SocialIcon);
export const DocumentsOption = homeOption('Documents', '/documents', 'documents', 'View your files', DocumentsIcon);
export const LinksOption = homeOption('Links', '/links', 'links', 'Your community links', LinksIcon);
export const OrderOption = homeOption('Order', '/order-status', 'orderStatus', 'Place an order or request', OrderIcon, BLUE_BAR);
export const PaymentsOption = homeOption('Payments', '/payments', 'payments', 'Manage cards & billing', PaymentIcon, BLUE_BAR);
export const SurveyOption = homeOption('Survey', '/survey', 'survey', 'Share feedback', SurveyIcon, BLUE_BAR);
export const HelloOption = homeOption('Hello!', '/hello', 'hello', 'Check in with your community', HelloIcon);
export const GreetingsOption = homeOption('Greetings', '/greetings', 'greetings', 'Say hi to family', GreetingsIcon);

export const CommunityNewsOption = neighborsOption('Community News', '/community-news', 'news');
export const AnnouncementsOption = neighborsOption('Announcements', '/announcements', 'announcements');

export const neighborsSidebarOptions: NeighborsOption[] = [
    CalendarOption,
    MenuOption,
    SocialOption,
    DocumentsOption,
    LinksOption,
    GreetingsOption,
    OrderOption,
    PaymentsOption,
    SurveyOption,
].map(toSidebarOption);

// No GreetingsOption here: the Hello! tile carries Greetings on the home page. It stays in
// neighborsSidebarOptions (and the /greetings route) so the page is still reachable.
export const neighborsHomeGridOptions: NeighborsHomeOption[] = [
    MenuOption,
    SocialOption,
    CalendarOption,
    OrderOption,
    PaymentsOption,
    DocumentsOption,
    LinksOption,
    HelloOption,
    SurveyOption,
];

// Guests only see a purpose-built subset of options - everything else here either
// has no guestAllowed <PrivateRoute> (see App.tsx) or isn't part of the guest flow yet.
const guestAllowedOptionPaths = new Set<string>([
    CalendarOption.path,
    MenuOption.path,
    OrderOption.path,
    PaymentsOption.path,
]);

export const getVisibleNeighborsOptions = <T extends NeighborsOption>(
    options: T[],
    userType: string | undefined,
    showPaymentsEntry: boolean,
): T[] => options
    .filter((option) => showPaymentsEntry || option.path !== PaymentsOption.path)
    .filter((option) => userType !== 'guest' || guestAllowedOptionPaths.has(option.path));